import {isFilled} from '@prismicio/client'
import {SliceZone} from '@prismicio/react'
import {ParentUid} from 'pages/[uid]/[subpageUid]'
import {
  ArticleDocument,
  BlogPostDocument,
  ButtonDocument,
  CustomerStoryDocument,
  DialogDocumentData,
  GenericFormDocument,
  NavigationDocumentData,
  PageDocument,
  ProductAnnouncementCardDocument,
  ProductDeepDiveDocumentData,
} from 'prismicio-types'
import {memo} from 'react'
import {components} from 'slices'
import {Article} from 'src/Article'
import {CustomerStory} from 'src/CustomerStory'
import {Layout} from 'src/common/Layout'
import {Meta} from 'src/common/Meta'

export const Page = memo(function Page(props: {
  articles: Array<ArticleDocument>
  customerStories: Array<CustomerStoryDocument>
  navigationData: NavigationDocumentData
  page: PageDocument | ArticleDocument | CustomerStoryDocument
  parentUids?: Array<ParentUid>
  productDeepDiveData: ProductDeepDiveDocumentData
  formData?: GenericFormDocument
  dialogData?: DialogDocumentData
  productAnnouncementsData?: Array<ProductAnnouncementCardDocument>
  moreCustomerStories?: Array<CustomerStoryDocument>
  buttonsData?: Array<ButtonDocument>
  categories?: Array<{category_name: string; value: string}>
  blogPosts?: Array<BlogPostDocument<string>>
  totalPages?: number
}) {
  if (props.page.type === 'article') {
    return (
      <Article
        navigationData={props.navigationData}
        page={props.page}
        parentUids={props.parentUids}
        breadcrumbs={props.page.data.breadcrumbs}
        buttonsData={props.buttonsData}
      />
    )
  }

  if (props.page.type === 'customer_story') {
    return (
      <CustomerStory
        navigationData={props.navigationData}
        page={props.page}
        parentUids={props.parentUids}
        breadcrumbs={props.page.data.breadcrumbs}
        moreCustomerStories={props.moreCustomerStories}
        buttonsData={props.buttonsData}
      />
    )
  }

  let mainClasses = ''

  if (props.page.data.column_layout) {
    mainClasses = 'flex flex-col md:flex-row'
  }

  return (
    <Layout
      alternateLanguages={
        props.parentUids
          ? props.page.alternate_languages.map((altLang) => ({
              ...altLang,
              parentUid: props.parentUids?.find((parentUid) => parentUid.lang === altLang.lang)?.uid,
            }))
          : props.page.alternate_languages
      }
      breadcrumbs={props.page.data.breadcrumbs}
      navigationData={props.navigationData}
      hideNavigation={!!props.page.data.is_navigation_hidden}
      mainClassName={mainClasses}
      isDarkNavigation={props.page.data.is_dark_navigation || false}
      pageUid={props.page.uid}
      locale={props.page.lang}
      parentUids={props.parentUids}
      forceFooterV2={props.page.data.force_footer_v2}
      funnelType={props.page.data.funnel_type}
      canonicalUrl={props.page.data.canonical_link}
    >
      <Meta
        {...(isFilled.keyText(props.page.data.meta_description) && {description: props.page.data.meta_description})}
        {...(isFilled.keyText(props.page.data.meta_title) && {title: props.page.data.meta_title})}
        {...(isFilled.image(props.page.data.meta_image) && {imageUrl: props.page.data.meta_image.url})}
        noIndex={props.page.data.disable_indexing}
      />
      <SliceZone
        components={components}
        context={{
          pageUid: props.page.uid,
          articles: props.articles,
          customerStories: props.customerStories,
          top_features: props.page.data.top_features,
          features_label: props.page.data.features_label,
          features: props.page.data.features,
          other_features_label: props.page.data.other_features_label,
          other_features: props.page.data.other_features,
          pricing_plan_1: props.page.data.pricing_plan_1,
          pricing_plan_2: props.page.data.pricing_plan_2,
          pricing_plan_3: props.page.data.pricing_plan_3,
          product_deep_dive_data: props.productDeepDiveData,
          navigation_data: props.navigationData,
          form_data: props.formData,
          dialog_data: props.dialogData,
          product_announcements_data: props.productAnnouncementsData,
          buttonsData: props.buttonsData,
          ...('category' in props.page.data && {category: props.page.data.category}),
          ...('date' in props.page.data && {publishedDate: props.page.data.date}),
          parentPage: props.page.data.parent_page,
          blogPosts: props.blogPosts,
          categories: props.categories,
          totalPages: props.totalPages,
        }}
        slices={props.page.data.slices}
      />
    </Layout>
  )
})
